import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import supabase from '../supabase';
import { addWatermark } from '../utils/watermark';
import watermarkLogo from '../winston-smiley-white.svg';
import AdminNav, { CREATOR_SIDEBAR_WIDTH, CREATOR_BREAKPOINT } from '../components/AdminNav';
import { theme, font, pillButton, outlineButton, inputStyle, labelStyle } from '../components/adminTheme';
import LoadingScreen from '../components/LoadingScreen';
import stripExif from '../utils/stripExif';
import { useLang } from '../useLang';
import { uploadToB2, listB2Files, deleteB2Files } from '../utils/uploadToB2';

function AlbumEditor() {
    const navigate = useNavigate();
    const { albumName } = useParams();
    const { t } = useLang();
    const [photos, setPhotos] = useState([]);
    const [selected, setSelected] = useState([]);
    const [selectMode, setSelectMode] = useState(false);
    const [album, setAlbum] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [addingPhotos, setAddingPhotos] = useState(false);
    const [editing, setEditing] = useState(false);
    const [editTitle, setEditTitle] = useState('');
    const [editLocation, setEditLocation] = useState('');
    const [editCreator, setEditCreator] = useState('');
    const [saving, setSaving] = useState(false);
    const [username, setUsername] = useState('');
    const [role, setRole] = useState('');
    const [avatarUrl, setAvatarUrl] = useState('');
    const [changingCover, setChangingCover] = useState(false);
    const [editDescription, setEditDescription] = useState('');
    const [editHeroUrl, setEditHeroUrl] = useState('');
    const [heroFile, setHeroFile] = useState(null);
    const [users, setUsers] = useState([]);
    const [authChecked, setAuthChecked] = useState(false);
    const [viewerIndex, setViewerIndex] = useState(null);

    useEffect(() => {
        const checkUser = async () => {
            const { data } = await supabase.auth.getSession();
            if (!data.session) { navigate('/admin'); return; }
            const { data: userData } = await supabase
                .from('users')
                .select('username, role, first_name, last_name, avatar_url')
                .eq('email', data.session?.user?.email)
                .single();
            if (userData) {
                setUsername(userData.username);
                setRole(userData.role);
                setAvatarUrl(userData.avatar_url || '');
            }
        };
        checkUser();
    }, [navigate]);

    const handleLogout = async () => {
        await supabase.auth.signOut();
        navigate('/admin');
    };

    useEffect(() => {
        const fetchData = async () => {
            const { data: sessionData } = await supabase.auth.getSession();
            if (!sessionData.session) return;

            const { data: userData } = await supabase
                .from('users')
                .select('username, role, first_name, last_name')
                .eq('email', sessionData.session.user.email)
                .single();
            if (!userData) return;

            const { data: albumData } = await supabase.from('albums').select('*').eq('folder', albumName).single();
            if (!albumData) { navigate('/admin/dashboard'); return; }

            const fullName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim();
            const isOwner = albumData.created_by === fullName;
            if (userData.role !== 'ADMIN' && !isOwner) {
                navigate('/admin/dashboard');
                return;
            }
            setAuthChecked(true);

            setAlbum(albumData);
            setEditTitle(albumData.title || '');
            setEditLocation(albumData.location || '');
            setEditCreator(albumData.created_by || '');
            setEditDescription(albumData.description || '');
            setEditHeroUrl(albumData.hero_url || '');

            const { data: usersData } = await supabase
                .from('users')
                .select('username, first_name, last_name')
                .order('username');
            if (usersData) setUsers(usersData);

            try {
                const files = await listB2Files(`photos/${albumData.region}/${albumName}`);
                setPhotos(files.map(f => ({ name: f.name, url: f.url, path: f.path })));
            } catch (err) {
                console.error('Erreur listage photos B2:', err);
            }
        };
        fetchData();
    }, [albumName, navigate]);

    const handleChangeCover = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setChangingCover(true);
        try {
            const cleanFile = await stripExif(file);
            const safeName = cleanFile.name.replace(/\s+/g, '-');
            const newCoverUrl = await uploadToB2(`covers/${album.folder}/${safeName}`, cleanFile);
            await supabase.from('albums').update({ cover_url: newCoverUrl }).eq('folder', albumName);
            setAlbum({ ...album, cover_url: newCoverUrl });
        } catch (err) {
            console.error('Erreur changement cover:', err);
        }
        setChangingCover(false);
    };

    const handleArchive = async () => {
        const newArchived = !album.archived;
        await supabase.from('albums').update({ archived: newArchived }).eq('folder', albumName);
        setAlbum({ ...album, archived: newArchived });
    };

    const handleAddPhotos = async (e) => {
        const newFiles = Array.from(e.target.files);
        if (newFiles.length === 0) return;
        setAddingPhotos(true);
        try {
            for (const file of newFiles) {
                const watermarkedFile = await addWatermark(file, watermarkLogo);
                await uploadToB2(`photos/${album.region}/${albumName}/${file.name}`, watermarkedFile);
            }
            const files = await listB2Files(`photos/${album.region}/${albumName}`);
            setPhotos(files.map(f => ({ name: f.name, url: f.url, path: f.path })));
        } catch (err) {
            console.error('Erreur ajout photos:', err);
        }
        setAddingPhotos(false);
    };

    const toggleSelect = (name) => {
        setSelected(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
    };

    const handlePhotoClick = (photo, index) => {
        if (selectMode) {
            toggleSelect(photo.name);
        } else {
            setViewerIndex(index);
        }
    };

    const handleDeleteSelected = async () => {
        if (selected.length === 0) return;
        if (!window.confirm(`${t('deleteCount')} ${selected.length} ${t('photosCount')}?`)) return;
        setDeleting(true);
        try {
            const toDelete = photos.filter(p => selected.includes(p.name));
            const paths = toDelete.map(p => p.path);
            await deleteB2Files(paths);
            setPhotos(photos.filter(p => !selected.includes(p.name)));
            setSelected([]);
        } catch (err) {
            console.error('Erreur suppression photos:', err);
        }
        setDeleting(false);
    };

    const handleSaveInfo = async () => {
        setSaving(true);
        try {
            let newHeroUrl = editHeroUrl;
            if (heroFile) {
                newHeroUrl = await uploadToB2(`covers/${album.folder}/hero_${heroFile.name}`, heroFile);
            }
            await supabase.from('albums').update({
                title: editTitle,
                location: editLocation,
                created_by: editCreator,
                description: editDescription,
                hero_url: newHeroUrl,
            }).eq('folder', albumName);
            setAlbum({ ...album, title: editTitle, location: editLocation, description: editDescription, hero_url: newHeroUrl });
            setHeroFile(null);
            setEditing(false);
        } catch (err) {
            console.error('Erreur sauvegarde infos:', err);
        }
        setSaving(false);
    };

    if (!authChecked) {
        return <LoadingScreen variant="light" />;
    }

    return (
        <div style={{ minHeight: '100vh', background: theme.bg }}>
            <style>{`
                .calbumed-content {
                    margin-left: ${CREATOR_SIDEBAR_WIDTH}px;
                    padding: 48px 40px 80px;
                    box-sizing: border-box;
                }
                @media (max-width: ${CREATOR_BREAKPOINT}px) {
                    .calbumed-content { margin-left: 0; padding: 88px 24px 60px; }
                }
                .calbumed-editcard {
                    background: ${theme.card};
                    border: 1px solid ${theme.border};
                    box-shadow: 0 1px 3px rgba(14,16,19,0.04);
                    padding: 20px;
                    margin-bottom: 24px;
                    display: grid;
                    grid-template-columns: 1fr 1fr 1fr;
                    gap: 14px;
                }
                @media (max-width: 700px) {
                    .calbumed-editcard { grid-template-columns: 1fr; }
                }
                .calbumed-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
                    gap: 4px;
                }
                .calbumed-card {
                    position: relative;
                    cursor: pointer;
                    box-sizing: border-box;
                    transition: opacity .15s ease;
                }
                .calbumed-cover {
                    position: relative;
                    width: 100%;
                    overflow: hidden;
                    background: #ece8df;
                }
                .calbumed-cover::before { content: ''; display: block; padding-top: 100%; }
                .calbumed-cover img {
                    position: absolute; top: 0; left: 0;
                    width: 100%; height: 100%; object-fit: cover; display: block;
                }
            `}</style>

            <AdminNav username={username} role={role} avatarUrl={avatarUrl} onLogout={handleLogout} activePath="/admin/dashboard" />

            <div className="calbumed-content">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
                    <div>
                        <p className="wlink" style={{ ...labelStyle, cursor: 'pointer', display: 'inline-block' }} onClick={() => navigate('/admin/dashboard')}>{t('back')}</p>
                        <h1 style={{ fontFamily: font, fontWeight: 700, fontSize: 'clamp(20px, 2.4vw, 26px)', letterSpacing: '-0.01em', color: theme.text, margin: '0 0 4px' }}>
                            {album?.title || albumName}
                        </h1>
                        <p style={{ fontFamily: font, fontSize: '13px', fontWeight: 500, color: theme.textMuted, margin: 0 }}>
                            {photos.length} {t('photosCount')} {selectMode && `· ${selected.length} ${t('selected')}`}
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <button className="wlink" style={outlineButton} onClick={() => setEditing(!editing)}>{t('editInfo')}</button>
                        <button className="wlink" style={outlineButton} onClick={() => document.getElementById('changeCoverInput').click()}>
                            {changingCover ? t('changingCover') : t('changeCover')}
                        </button>
                        <input id="changeCoverInput" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleChangeCover} />
                        <button className="wlink" style={outlineButton} onClick={handleArchive}>
                            {album?.archived ? t('archived') : t('visible')}
                        </button>
                        <button
                            className="wlink"
                            style={outlineButton}
                            onClick={() => { setSelectMode(!selectMode); setSelected([]); }}
                        >
                            {selectMode ? t('cancelSelection') : t('selectPhotos')}
                        </button>
                        <button className="wlink" style={pillButton} onClick={() => document.getElementById('addPhotosInput').click()}>
                            {addingPhotos ? '...' : t('addPhotos')}
                        </button>
                        <input id="addPhotosInput" type="file" multiple accept="image/*" style={{ display: 'none' }} onChange={handleAddPhotos} />
                        {selectMode && selected.length > 0 && (
                            <button
                                onClick={handleDeleteSelected}
                                disabled={deleting}
                                className="wlink"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: font, fontSize: '12.5px', fontWeight: 700, color: theme.danger }}
                            >
                                {deleting ? '...' : `${t('deleteCount')} ${selected.length}`}
                            </button>
                        )}
                    </div>
                </div>

                {editing && (
                    <div className="calbumed-editcard">
                        <div>
                            <p style={{ ...labelStyle, margin: '0 0 6px' }}>{t('title')}</p>
                            <input type="text" value={editTitle} onChange={e => setEditTitle(e.target.value)} style={{ ...inputStyle, fontSize: '13px' }} />
                        </div>
                        <div>
                            <p style={{ ...labelStyle, margin: '0 0 6px' }}>{t('location')}</p>
                            <input type="text" value={editLocation} onChange={e => setEditLocation(e.target.value)} style={{ ...inputStyle, fontSize: '13px' }} />
                        </div>
                        <div>
                            <p style={{ ...labelStyle, margin: '0 0 6px' }}>{t('creatorLabel')}</p>
                            {role === 'ADMIN' ? (
                                <select value={editCreator} onChange={e => setEditCreator(e.target.value)} style={{ ...inputStyle, fontSize: '13px' }}>
                                    <option value="">{t('selectCreator')}</option>
                                    {users.map(u => {
                                        const label = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username;
                                        return <option key={u.username} value={label}>{label} (@{u.username})</option>;
                                    })}
                                </select>
                            ) : (
                                <p style={{ fontFamily: font, fontSize: '13px', fontWeight: 500, color: theme.text, padding: '9px 0', margin: 0 }}>{editCreator}</p>
                            )}
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <p style={{ ...labelStyle, margin: '0 0 6px' }}>{t('descriptionLabel')}</p>
                            <textarea value={editDescription} onChange={e => setEditDescription(e.target.value)} rows={3} style={{ ...inputStyle, fontSize: '13px', resize: 'vertical' }} />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <p style={{ ...labelStyle, margin: '0 0 6px' }}>{t('heroLabel')}</p>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <input type="text" value={editHeroUrl} onChange={e => setEditHeroUrl(e.target.value)} placeholder={t('heroUrlPlaceholder')} style={{ ...inputStyle, flex: 1, fontSize: '13px' }} />
                                <button className="wlink" style={outlineButton} onClick={() => document.getElementById('heroFileInput').click()}>
                                    {heroFile ? heroFile.name : t('uploadBtn')}
                                </button>
                                <input id="heroFileInput" type="file" accept="image/*" style={{ display: 'none' }} onChange={e => setHeroFile(e.target.files[0])} />
                            </div>
                            {(heroFile || editHeroUrl) && (
                                <img src={heroFile ? URL.createObjectURL(heroFile) : editHeroUrl} alt="hero preview" style={{ marginTop: '10px', width: '100%', height: '100px', objectFit: 'cover' }} />
                            )}
                        </div>
                        <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: '20px' }}>
                            <button className="wlink" style={outlineButton} onClick={() => setEditing(false)}>{t('cancel')}</button>
                            <button className="wlink" style={pillButton} onClick={handleSaveInfo} disabled={saving}>{saving ? t('saving') : t('save')}</button>
                        </div>
                    </div>
                )}

                <div className="calbumed-grid">
                    {photos.map((photo, i) => (
                        <div
                            key={photo.name}
                            onClick={() => handlePhotoClick(photo, i)}
                            className="calbumed-card"
                            style={{
                                border: selected.includes(photo.name) ? `2px solid ${theme.text}` : `1px solid ${theme.border}`,
                                opacity: selectMode && selected.includes(photo.name) ? 0.7 : 1,
                            }}
                        >
                            <div className="calbumed-cover">
                                <img src={photo.url} alt={photo.name} />
                                {selectMode && selected.includes(photo.name) && (
                                    <div style={{ position: 'absolute', top: '8px', right: '8px', background: theme.text, color: '#fff', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>✓</div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {viewerIndex !== null && (
                <div
                    onClick={() => setViewerIndex(null)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(15,14,12,0.95)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    <button
                        onClick={(e) => { e.stopPropagation(); setViewerIndex(null); }}
                        style={{ position: 'absolute', top: '20px', right: '24px', background: 'rgba(255,255,255,0.1)', border: 'none', width: '36px', height: '36px', color: 'white', fontSize: '16px', cursor: 'pointer' }}
                    >
                        ✕
                    </button>

                    {viewerIndex > 0 && (
                        <button
                            onClick={(e) => { e.stopPropagation(); setViewerIndex(viewerIndex - 1); }}
                            style={{ position: 'absolute', left: '20px', background: 'rgba(255,255,255,0.1)', border: 'none', width: '44px', height: '44px', color: 'white', fontSize: '20px', cursor: 'pointer' }}
                        >
                            ‹
                        </button>
                    )}
                    {viewerIndex < photos.length - 1 && (
                        <button
                            onClick={(e) => { e.stopPropagation(); setViewerIndex(viewerIndex + 1); }}
                            style={{ position: 'absolute', right: '20px', background: 'rgba(255,255,255,0.1)', border: 'none', width: '44px', height: '44px', color: 'white', fontSize: '20px', cursor: 'pointer' }}
                        >
                            ›
                        </button>
                    )}

                    <img
                        src={photos[viewerIndex].url}
                        alt={photos[viewerIndex].name}
                        onClick={(e) => e.stopPropagation()}
                        style={{ maxWidth: '88vw', maxHeight: '86vh', objectFit: 'contain' }}
                    />

                    <p style={{ position: 'absolute', bottom: '20px', color: 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: 500 }}>
                        {viewerIndex + 1} / {photos.length}
                    </p>
                </div>
            )}
        </div>
    );
}

export default AlbumEditor;